// src/App.tsx
import { useState, useEffect } from 'react';
import {
	BrowserRouter,
	Routes,
	Route,
	useNavigate,
	useLocation,
} from 'react-router-dom';
import {
	AppShell,
	Container,
	Title,
	Paper,
	Stack,
	Text,
	Burger,
	Group,
	Box,
	useMantineColorScheme,
	MantineTheme,
} from '@mantine/core';
import {
	IconPigMoney,
	IconReceipt2,
	IconChartBar,
	IconSettings,
	IconSparkles,
	Icon,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import TotalDisplay from './components/TotalDisplay';
import AddBillForm from './components/AddBillForm';
import BillList from './components/BillList';
import BudgetAnalytics from './components/BudgetAnalytics';
import ForYouPage from './components/ForYouPage';
import Settings from './components/Settings';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import ErrorBoundary from './components/ErrorBoundary';
import { useLocalBills } from './hooks/useLocalBills';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Bill } from './types';

interface NavItem {
	icon: Icon;
	label: string;
	color: string;
	id: string;
	path: string;
}

function AppContent() {
	const {
		bills,
		pastDueBills,
		setBills,
		addBill,
		updateBill,
		toggleBillPaid,
		deleteBill,
		markPastDueBillPaid,
		reorderBills,
	} = useLocalBills();

	// Version check for TWA auto-updates
	const { version, checkForUpdates, isUpdating } = useVersionCheck();

	const [currentPage, setCurrentPage] = useState(() => {
		return localStorage.getItem('bill-tracker-page') || 'bills';
	});

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		// Update currentPage based on route
		const path = location.pathname;
		if (path === '/') setCurrentPage('bills');
		else if (path === '/settings') setCurrentPage('settings');
		else if (path === '/analytics') setCurrentPage('analytics');
		else if (path === '/for-you') setCurrentPage('for-you');
	}, [location]);

	useEffect(() => {
		localStorage.setItem('bill-tracker-page', currentPage);
	}, [currentPage]);

	const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
	const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure();
	const { colorScheme } = useMantineColorScheme();

	const handleRestoreBills = (restoredBills: Bill[]) => {
		setBills(restoredBills);
		setCurrentPage('bills');
		navigate('/');

		notifications.show({
			title: 'Data Restored',
			message: 'Your bills have been restored successfully.',
			color: 'green',
		});
	};

	const navigation: NavItem[] = [
		{
			icon: IconReceipt2,
			label: 'Bills',
			color: 'blue',
			id: 'bills',
			path: '/',
		},
		{
			icon: IconSparkles,
			label: 'For You',
			color: 'violet',
			id: 'for-you',
			path: '/for-you',
		},
		{
			icon: IconChartBar,
			label: 'Budget Analytics',
			color: 'teal',
			id: 'analytics',
			path: '/analytics',
		},
		{
			icon: IconSettings,
			label: 'Settings',
			color: 'gray',
			id: 'settings',
			path: '/settings',
		},
	];

	const renderMainContent = () => {
		switch (currentPage) {
			case 'bills':
				return (
					<ErrorBoundary fallbackTitle='Bills View Error'>
						<Box
							h='100%'
							display='flex'
							style={{ flexDirection: 'column' }}
						>
							<Box mb='md'>
								<TotalDisplay
									bills={bills}
									pastDueBills={pastDueBills}
									onMarkPastDuePaid={markPastDueBillPaid}
								/>
							</Box>
							<Box mb='0px'>
								<AddBillForm onAddBill={addBill} />
							</Box>
							<Box
								style={{
									flex: 1,
									minHeight: 0,
									position: 'relative',
								}}
							>
								<BillList
									bills={bills}
									onTogglePaid={toggleBillPaid}
									onDelete={(billId: string) => {
										deleteBill(billId);
										notifications.show({
											title: 'Bill Deleted',
											message:
												'The bill has been removed from your list.',
											color: 'blue',
										});
									}}
									onUpdate={(updatedBill: Bill) => {
										updateBill(updatedBill);
										notifications.show({
											title: 'Bill Updated',
											message:
												'The bill has been updated successfully.',
											color: 'blue',
										});
									}}
									onReorder={reorderBills}
								/>
							</Box>
						</Box>
					</ErrorBoundary>
				);
			case 'settings':
				return (
					<Settings
						bills={bills}
						onRestoreBills={handleRestoreBills}
						onCheckForUpdates={checkForUpdates}
						isCheckingUpdates={isUpdating}
						appVersion={version}
					/>
				);
			case 'analytics':
				return (
					<ErrorBoundary fallbackTitle='Analytics Error'>
						<BudgetAnalytics
							bills={bills}
							pastDueBills={pastDueBills}
						/>
					</ErrorBoundary>
				);
			case 'for-you':
				return (
					<ErrorBoundary fallbackTitle='For You Error'>
						<ForYouPage
							bills={bills}
							pastDueBills={pastDueBills}
							onUpdateBill={updateBill}
						/>
					</ErrorBoundary>
				);
			default:
				return (
					<Paper withBorder p='md' radius='md'>
						<Text ta='center' size='lg' c='dimmed'>
							Coming soon...
						</Text>
					</Paper>
				);
		}
	};

	return (
		<AppShell
			header={{ height: 60 }}
			navbar={{
				width: 300,
				breakpoint: 'sm',
				collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
			}}
			padding='md'
			styles={(theme: MantineTheme) => ({
				main: {
					backgroundColor:
						colorScheme === 'dark'
							? theme.colors.dark[8]
							: theme.colors.gray[0],
					height: 'calc(100vh - 60px)',
					overflow: 'hidden',
				},
				navbar: {
					background:
						colorScheme === 'dark'
							? 'rgba(26, 27, 30, 0.8)'
							: 'rgba(255, 255, 255, 0.8)',
					backdropFilter: 'blur(10px)',
					WebkitBackdropFilter: 'blur(10px)',
					borderRight: `1px solid ${
						colorScheme === 'dark'
							? theme.colors.dark[4]
							: theme.colors.gray[2]
					}`,
					boxShadow: theme.shadows.sm,
				},
			})}
		>
			<AppShell.Header>
				<Group h='100%' px='md'>
					<Burger
						opened={mobileOpened}
						onClick={toggleMobile}
						hiddenFrom='sm'
						size='sm'
					/>
					<Burger
						opened={desktopOpened}
						onClick={toggleDesktop}
						visibleFrom='sm'
						size='sm'
					/>
					<Group gap='xs'>
						<Title order={3} fw={700} size='h3'>
							Bill Tracker
						</Title>
						<IconPigMoney
							size={30}
							stroke={1.5}
							style={{ color: '#4F46E5' }}
						/>
					</Group>
				</Group>
			</AppShell.Header>

			<AppShell.Navbar p='md'>
				<AppShell.Section>
					<Text size='xs' fw={500} c='dimmed' mb='xs' tt='uppercase'>
						Navigation
					</Text>
					{navigation.map((item) => (
						<Group
							key={item.label}
							p='xs'
							style={{
								cursor: 'pointer',
								backgroundColor:
									currentPage === item.id
										? colorScheme === 'dark'
											? 'var(--mantine-color-dark-5)'
											: 'var(--mantine-color-gray-1)'
										: 'transparent',
								borderRadius: 'var(--mantine-radius-sm)',
							}}
							onClick={() => {
								setCurrentPage(item.id);
								navigate(item.path);
								if (mobileOpened) {
									toggleMobile();
								}
							}}
						>
							<item.icon
								size={20}
								color={`var(--mantine-color-${item.color}-6)`}
							/>
							<Text
								size='sm'
								fw={500}
								c={
									currentPage === item.id &&
									colorScheme === 'dark'
										? 'white'
										: undefined
								}
							>
								{item.label}
							</Text>
						</Group>
					))}
				</AppShell.Section>

				<AppShell.Section mt='auto'>
					<Text size='xs' fw={500} c='dimmed' mb='xs' tt='uppercase'>
						App Info
					</Text>
					<Text size='xs' fw={500} mb='xs'>
						99 Solutions
					</Text>
					<Text size='xs' c='dimmed'>
						Version: {version}
					</Text>
					<Stack gap={4} mt='xs'>
						<Text
							component='span'
							size='xs'
							c='dimmed'
							style={{ cursor: 'pointer' }}
							onClick={() => {
								navigate('/privacy');
								if (mobileOpened) toggleMobile();
							}}
						>
							Privacy Policy
						</Text>
						<Text
							component='span'
							size='xs'
							c='dimmed'
							style={{ cursor: 'pointer' }}
							onClick={() => {
								navigate('/terms');
								if (mobileOpened) toggleMobile();
							}}
						>
							Terms of Service
						</Text>
						<Text
							component='span'
							size='xs'
							c='dimmed'
							style={{ cursor: 'pointer' }}
							onClick={() => {
								navigate('/cookie-policy');
								if (mobileOpened) toggleMobile();
							}}
						>
							Cookie Policy
						</Text>
					</Stack>
				</AppShell.Section>
			</AppShell.Navbar>

			<AppShell.Main>
				<Container
					size='sm'
					h='100%'
					p='0'
					style={{ paddingBottom: '16px' }}
				>
					<Routes>
						<Route path='/' element={renderMainContent()} />
						<Route path='/settings' element={renderMainContent()} />
						<Route
							path='/analytics'
							element={renderMainContent()}
						/>
						<Route
							path='/for-you'
							element={renderMainContent()}
						/>
						<Route path='/privacy' element={<PrivacyPolicy />} />
						<Route path='/terms' element={<TermsOfService />} />
						<Route
							path='/cookie-policy'
							element={<CookiePolicy />}
						/>
					</Routes>
				</Container>
			</AppShell.Main>
		</AppShell>
	);
}

function App() {
	return (
		<BrowserRouter>
			<AppContent />
		</BrowserRouter>
	);
}

export default App;
